<template>
  <div
    id="revenue_statistics_page"
    class="card"
  >
    <b-card-header>
      <ps-filters
        v-model="filterValues"
        :filter-settings="filterSettings"
        :show-export="$hasPermission('export_revenue_stats')"
        @search="search"
        @exportToCSV="exportToCSV"
      />
    </b-card-header>
    <div class="table-responsive mb-0">
      <table
        role="table"
        class="table b-table table-striped table-hover"
      >
        <thead
          role="rowgroup"
          class=""
        >
          <tr
            role="row"
            class=""
          >
            <th
              v-if="groupBy === 'app'"
              role="columnheader"
              aria-colindex="1"
            >
              {{ $t('logs.app') }}
            </th>
            <th
              v-if="groupBy === 'date'"
              role="columnheader"
              aria-colindex="1"
              :aria-sort="arialSort('date')"
              @click="sortCol('date')"
            >
              {{ $t('revenuestatistics.date') }}
            </th>
            <th
              v-if="groupBy === 'country'"
              role="columnheader"
              aria-colindex="1"
              :aria-sort="arialSort('country_code')"
              @click="sortCol('country_code')"
            >
              {{ $t('dashboard.country') }}
            </th>
            <th
              role="columnheader"
              aria-colindex="3"
              :aria-sort="arialSort('monetized_users')"
              @click="sortCol('monetized_users')"
            >
              {{ $t('revenuestatistics.monetizedUsers') }}
            </th>
            <th
              role="columnheader"
              aria-colindex="3"
              :aria-sort="arialSort('survey_monetized_users')"
              @click="sortCol('survey_monetized_users')"
            >
              {{ $t('revenuestatistics.monetizedUsersSurveys') }}
            </th>
            <th
              role="columnheader"
              aria-colindex="3"
              :aria-sort="arialSort('offers_monetized_users')"
              @click="sortCol('offers_monetized_users')"
            >
              {{ $t('revenuestatistics.monetizedUsersOffers') }}
            </th>
            <th
              role="columnheader"
              aria-colindex="3"
              :aria-sort="arialSort('survey_revenue')"
              @click="sortCol('survey_revenue')"
            >
              {{ $t('revenuestatistics.surveyRevenue') }}
            </th>
            <th
              role="columnheader"
              aria-colindex="3"
              :aria-sort="arialSort('offers_revenue')"
              @click="sortCol('offers_revenue')"
            >
              {{ $t('dashboard.offersRevenue') }}
            </th>
            <th
              role="columnheader"
              aria-colindex="3"
              :aria-sort="arialSort('reconciled_revenue')"
              @click="sortCol('reconciled_revenue')"
            >
              {{ $t('revenuestatistics.reconciledRevenue') }}
            </th>
            <th
              role="columnheader"
              aria-colindex="3"
              :aria-sort="arialSort('other_revenue')"
              @click="sortCol('other_revenue')"
            >
              {{ $t('revenuestatistics.otherRevenue') }}
            </th>
            <th
              role="columnheader"
              aria-colindex="3"
              :aria-sort="arialSort('total_revenue')"
              @click="sortCol('total_revenue')"
            >
              {{ $t('revenuestatistics.totalRevenue') }}
            </th>
          </tr>
        </thead>
        <tbody role="rowgroup">
          <template v-for="(rowTr, indextr) in items">
            <tr
              :key="indextr"
              :class="rowTr.class"
            >
              <td v-if="groupBy === 'app'">
                <app-link :app="rowTr" />
              </td>
              <td v-if="groupBy === 'date'">
                {{ rowTr.date | dotDateTimeShort }}
              </td>
              <td v-if="groupBy === 'country'">
                {{ rowTr.country_name }}
              </td>
              <td>
                {{ rowTr.monetized_users | formatCount }}
              </td>
              <td>
                {{ rowTr.survey_monetized_users | formatCount }}
              </td>
              <td>
                {{ rowTr.offers_monetized_users | formatCount }}
              </td>
              <td :class="rowTr.survey_revenue < 0 ? 'text-danger' : 'text-success'">
                {{ rowTr.survey_revenue | formatCurrency }}
              </td>
              <td :class="rowTr.offers_revenue < 0 ? 'text-danger' : 'text-success'">
                {{ rowTr.offers_revenue | formatCurrency }}
              </td>
              <td :class="rowTr.reconciled_revenue < 0 ? 'text-danger' : 'text-success'">
                {{ rowTr.reconciled_revenue | formatCurrency }}
              </td>
              <td :class="rowTr.other_revenue < 0 ? 'text-danger' : 'text-success'">
                {{ rowTr.other_revenue | formatCurrency }}
              </td>
              <td :class="rowTr.total_revenue < 0 ? 'text-danger' : 'text-success'">
                {{ rowTr.total_revenue | formatCurrency }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <b-card-footer>
      <b-row>
        <b-col
          v-show="items.length > 0"
          cols="12"
        >
          <pagination-and-table-info
            :data="paginationData"
            @change="handleChangePage($event)"
          />
        </b-col>
        <b-col
          v-show="items.length === 0"
          cols="12"
          style="font-weight: 500; text-align: center"
        >
          {{ $t('apps.noData') }}
        </b-col>
      </b-row>
    </b-card-footer>
  </div>
</template>

<script>
import i18n from '@/libs/i18n'

import PaginationAndTableInfo from '@ps_main/custom-components/PaginationAndTableInfo.vue'
import PsFilters from '@/main/custom-components/PsFilters.vue'
import AppLink from '@/main/custom-components/AppLink.vue'

export default {
  components: {
    AppLink,
    PsFilters,
    PaginationAndTableInfo,
  },
  data() {
    const $this = this

    return {
      items: [],
      params: {
        page: 1,
        orderBy: 'total_revenue',
        itemPerPage: 500,
        orderDirection: 'desc',
        initFilter: 0,
      },
      filterValues: {},
      filterSettings: {
        dateRange: {
          type: 'daterange',
          get label() { return i18n.t('logs.fromTo') },
          class: 'mr-2 mb-0',
          style: '',
          data: [],
          defaultValue: {
            fromDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
            toDate: new Date(),
          },
          'label-cols': 2,
        },
        app: {
          type: 'select',
          get label() { return i18n.t('apps.appName') },
          class: 'mr-2 mb-0',
          style: 'width: 320px;',
          data: [],
          defaultData: { value: null, get text() { return i18n.t('logs.allApps') } },
          defaultValue: null,
          'label-cols': 4,
        },
        country: {
          type: 'select',
          get label() { return i18n.t('revenuestatistics.country') },
          class: 'mr-2 mb-0',
          style: 'width: 320px;',
          data: [],
          defaultData: { value: null, get text() { return i18n.t('revenuestatistics.allCountries') } },
          defaultValue: null,
          'label-cols': 3,
        },
        group_by: {
          type: 'radio',
          get label() { return i18n.t('revenuestatistics.groupBy') },
          class: 'mr-2',
          'label-cols': 2,
          'content-cols': 10,
          style: 'width: 400px;',
          defaultValue: 'app',
          data: [
            {
              value: 'app',
              get text() { return i18n.t('logs.app') },
            },
            {
              value: 'country',
              get text() { return i18n.t('dashboard.country') },
            },
            {
              value: 'date',
              get text() { return i18n.t('revenuestatistics.day') },
            },
          ],
          change(value) {
            $this.selectedGroupBy = value
          },
          refresh_uuid: null,
        },
      },
      pageTotal: 0,
      totalItems: 0,
      responseData: null,
      selectedGroupBy: 'app',
      groupBy: 'app',
    }
  },
  computed: {
    paginationData() {
      return this.laravelPaginationPages(this.responseData)
    },
  },
  methods: {
    handleChangePage(page) {
      this.params.page = page
      this.search()
    },
    sortCol(fieldName) {
      if (this.params.orderBy === fieldName) {
        this.params.orderDirection = this.params.orderDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.params.orderBy = fieldName
        this.params.orderDirection = 'asc'
      }
      this.search()
    },
    arialSort(sortFieldName) {
      if (this.params.orderBy === sortFieldName) {
        return this.params.orderDirection === 'asc'
          ? 'ascending'
          : 'descending'
      }
      return 'none'
    },
    search() {
      this.showLoading()
      this.$http
        .get('/api/statistics/revenue', {
          params: {
            ...this.params,
            ...this.filterValues,
          },
        })
        .then(response => {
          this.groupBy = this.selectedGroupBy
          const responseData = response.data
          this.items = responseData.data
          this.responseData = responseData
          this.initiateFilters(responseData)
        })
        .catch(errors => {
          this.items = []
          this.responseData = null
          this.alertApiError(errors, i18n.t('revenuestatistics.revenueStatisticsRequestFailed'))
        })
        .finally(() => {
          this.hideLoading()
        })
    },
    initiateFilters(responseData) {
      if (
        this.params.initFilter === 0
          && typeof responseData.filterData !== 'undefined'
      ) {
        Object.keys(responseData.filterData).forEach(property => {
          if (typeof this.filterSettings[property] === 'object') {
            if (this.filterSettings[property].defaultData !== undefined) {
              this.filterSettings[property].data = [
                ...[this.filterSettings[property].defaultData],
                ...responseData.filterData[property],
              ]
            } else {
              this.filterSettings[property].data = responseData.filterData[property]
            }
          }
        })

        this.params.initFilter = 1
      }
    },
    exportToCSV() {
      const exportLocale = i18n.locale
      this.isBusy = true
      this.showLoading()
      this.$http
        .get('/api/statistics/revenue/export', {
          params: {
            ...this.params,
            ...this.filterValues,
            locale: exportLocale,
            only_file_name: true,
          },
        })
        .then(response => {
          const filename = response.data

          return this.$http
            .get('/api/statistics/revenue/export', {
              params: {
                ...this.params,
                ...this.filterValues,
                locale: exportLocale,
              },
              responseType: 'blob',
            })
            .then(responseExport => {
              const url = window.URL.createObjectURL(new Blob([responseExport.data]))
              const link = document.createElement('a')
              link.href = url
              link.setAttribute('download', filename)
              document.body.appendChild(link)
              link.click()
            })
            .catch(errors => {
              this.alertApiError(errors, i18n.t('revenuestatistics.revenueExportFailed'))
            })
        })
        .catch(errors => {
          this.alertApiError(errors, i18n.t('revenuestatistics.revenueExportFailed'))
        })
        .finally(() => {
          this.hideLoading()
          this.isBusy = false
        })
    },
  },
}
</script>
